<template>
  <div class="shipment-document-form">
    <v-row>
      <v-col cols="12" md="4">
        <v-textarea
          v-model="local.seller"
          rows="3"
          auto-grow
          :label="$t('STOCK.TRADE_DOCUMENT.SELLER')"
          :disabled="disabled"
        ></v-textarea>
      </v-col>
      <v-col cols="12" md="4">
        <v-textarea
          v-model="local.boo"
          rows="3"
          auto-grow
          :label="$t('STOCK.TRADE_DOCUMENT.BOO')"
          :disabled="disabled"
        ></v-textarea>
      </v-col>
      <v-col cols="12" md="4">
        <v-textarea
          v-model="local.consignee"
          rows="3"
          auto-grow
          :label="$t('STOCK.TRADE_DOCUMENT.CONSIGNEE')"
          :disabled="disabled"
        ></v-textarea>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="local.terms_of_delivery"
          :label="$t('STOCK.TRADE_DOCUMENT.TERMS_OF_DELIVERY')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="4">
        <DatePicker
          v-model="local.date_of_loading"
          :label="$t('STOCK.TRADE_DOCUMENT.DATE_OF_LOADING')"
          without-hint
          clearable
          :disabled="disabled"
        ></DatePicker>
      </v-col>
      <v-col cols="12" md="4">
        <v-textarea
          v-model="local.destination"
          rows="2"
          auto-grow
          :label="$t('STOCK.TRADE_DOCUMENT.DESTINATION')"
          :disabled="disabled"
        ></v-textarea>
      </v-col>
    </v-row>

    <v-subheader class="px-0">{{ $t("STOCK.TRADE_DOCUMENT.GROUP_ORIGIN") }}</v-subheader>
    <v-row>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="local.origin.born"
          :label="$t('STOCK.TRADE_DOCUMENT.ORIGIN_BORN')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="local.origin.reared"
          :label="$t('STOCK.TRADE_DOCUMENT.ORIGIN_REARED')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="local.origin.slaughtered"
          :label="$t('STOCK.TRADE_DOCUMENT.ORIGIN_SLAUGHTERED')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="local.origin.country_of_origin"
          :label="$t('STOCK.TRADE_DOCUMENT.COUNTRY_OF_ORIGIN')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-subheader class="px-0">{{ $t("STOCK.TRADE_DOCUMENT.GROUP_GOODS") }}</v-subheader>
    <p class="caption grey--text text--darken-1 mb-2">{{ $t("STOCK.TRADE_DOCUMENT.GOODS_LOT_HINT") }}</p>
    <div class="trade-goods-list mb-3">
      <div v-for="(line, index) in local.goods_lines" :key="line._key" class="trade-goods-line">
        <v-row align="center">
          <v-col>
            <v-text-field
              v-model="line.name"
              :label="$t('STOCK.TRADE_DOCUMENT.GOODS_NAME')"
              dense
              hide-details
              :disabled="disabled"
            ></v-text-field>
          </v-col>
          <v-col cols="auto" class="text-no-wrap">
            <v-btn
              icon
              small
              :disabled="disabled"
              :title="$t('STOCK.TRADE_DOCUMENT.ADD_LOT')"
              @click="cloneGoodsLine(index)"
            >
              <v-icon small>mdi-plus</v-icon>
            </v-btn>
            <v-btn icon small :disabled="disabled" @click="removeGoodsLine(index)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-row class="trade-goods-details">
          <v-col cols="12" md="2">
            <StockProductPicker
              :value="line.stock_product_id"
              :label="$t('STOCK.PRODUCTS.PRODUCT')"
              :show-category-filter="false"
              :disabled="disabled"
              @input="onProductPicked(line, $event)"
            />
          </v-col>
          <v-col cols="6" sm="4" md="2">
            <v-text-field
              v-model="line.hs_code"
              :label="$t('STOCK.TRADE_DOCUMENT.HS_CODE')"
              dense
              hide-details
              :disabled="disabled"
            ></v-text-field>
          </v-col>
          <v-col cols="6" sm="4" md="2">
            <v-text-field
              v-model="line.lot_number"
              :label="$t('STOCK.TRADE_DOCUMENT.LOT_BATCH_NUMBERS')"
              dense
              hide-details
              :disabled="disabled"
            ></v-text-field>
          </v-col>
          <v-col cols="6" sm="4" md="2">
            <DatePicker
              v-model="line.expiry_date"
              :label="$t('STOCK.TRADE_DOCUMENT.BEST_BEFORE')"
              dense
              hide-details
              without-hint
              without-icon
              clearable
              :disabled="disabled"
            ></DatePicker>
          </v-col>
          <v-col cols="6" sm="4" md="2">
            <v-text-field
              v-model="line.quantity_kg"
              :label="$t('STOCK.TRADE_DOCUMENT.QUANTITY')"
              type="number"
              dense
              hide-details
              suffix="kg"
              :disabled="disabled"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="8" md="2">
            <v-text-field
              v-model="line.origin"
              :label="$t('STOCK.TRADE_DOCUMENT.LINE_ORIGIN')"
              dense
              hide-details
              :disabled="disabled"
            ></v-text-field>
          </v-col>
        </v-row>
      </div>
    </div>
    <v-btn small outlined color="primary" class="mb-4" :disabled="disabled" @click="addGoodsLine">
      <v-icon left small>mdi-plus</v-icon>
      {{ $t("STOCK.TRADE_DOCUMENT.ADD_GOODS_LINE") }}
    </v-btn>

    <v-row>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="local.pallets_boxes"
          :label="$t('STOCK.TRADE_DOCUMENT.PALLETS_BOXES')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="local.net_weight_kg"
          type="number"
          suffix="kg"
          :label="$t('STOCK.TRADE_DOCUMENT.NET_WEIGHT')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="local.gross_weight_kg"
          type="number"
          suffix="kg"
          :label="$t('STOCK.TRADE_DOCUMENT.GROSS_WEIGHT')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="local.truck_no"
          :label="$t('STOCK.TRADE_DOCUMENT.TRUCK_NO')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-textarea
          v-model="local.place_of_loading"
          rows="2"
          auto-grow
          :label="$t('STOCK.TRADE_DOCUMENT.PLACE_OF_LOADING')"
          :disabled="disabled"
        ></v-textarea>
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="local.coldstore_number"
          :label="$t('STOCK.TRADE_DOCUMENT.COLDSTORE_NUMBER')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <DatePicker
          v-model="local.issued_date"
          :label="$t('STOCK.TRADE_DOCUMENT.ISSUED_DATE')"
          without-hint
          clearable
          :disabled="disabled"
        ></DatePicker>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-textarea
          v-model="local.slaughterhouses"
          rows="2"
          auto-grow
          :label="$t('STOCK.TRADE_DOCUMENT.SLAUGHTERHOUSES')"
          :disabled="disabled"
        ></v-textarea>
      </v-col>
      <v-col cols="12" md="6">
        <v-textarea
          v-model="local.cutting_plant"
          rows="2"
          auto-grow
          :label="$t('STOCK.TRADE_DOCUMENT.CUTTING_PLANT')"
          :disabled="disabled"
        ></v-textarea>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-textarea
          v-model="local.date_of_slaughter_freezing"
          rows="2"
          auto-grow
          :label="$t('STOCK.TRADE_DOCUMENT.DATE_OF_SLAUGHTER_FREEZING')"
          :disabled="disabled"
        ></v-textarea>
      </v-col>
      <v-col cols="12" md="6">
        <v-textarea
          v-model="local.production_date"
          rows="2"
          auto-grow
          :label="$t('STOCK.TRADE_DOCUMENT.PRODUCTION_DATE')"
          :disabled="disabled"
        ></v-textarea>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="local.keep_frozen"
          :label="$t('STOCK.TRADE_DOCUMENT.KEEP_FROZEN')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="local.transportation_temperature"
          :label="$t('STOCK.TRADE_DOCUMENT.TRANSPORTATION_TEMPERATURE')"
          :disabled="disabled"
        ></v-text-field>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import DatePicker from "@/views/partials/form/DatePicker.vue";
import StockProductPicker from "@/views/partials/stock/StockProductPicker.vue";
import { GET_STOCK_PRODUCT } from "@/store/stock/stockProducts.module";

const emptyOrigin = () => ({
  born: "",
  reared: "",
  slaughtered: "",
  country_of_origin: "",
});

const emptyGoodsLine = () => ({
  _key: `trade-goods-${Date.now()}-${Math.random()}`,
  stock_product_id: null,
  name: "",
  hs_code: "",
  lot_number: "",
  expiry_date: null,
  quantity_kg: null,
  origin: "",
});

const emptyForm = () => ({
  seller: "",
  boo: "",
  consignee: "",
  terms_of_delivery: "",
  date_of_loading: null,
  origin: emptyOrigin(),
  destination: "",
  goods_lines: [emptyGoodsLine()],
  pallets_boxes: "",
  net_weight_kg: null,
  gross_weight_kg: null,
  place_of_loading: "",
  coldstore_number: "",
  slaughterhouses: "",
  cutting_plant: "",
  lot_batch_numbers: "",
  date_of_slaughter_freezing: "",
  production_date: "",
  best_before: "",
  truck_no: "",
  keep_frozen: "",
  transportation_temperature: "",
  issued_date: null,
});

export default {
  name: "StockTradeDocumentForm",
  components: { DatePicker, StockProductPicker },
  props: {
    form: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      local: emptyForm(),
      hydrating: false,
      dirty: false,
      lastSignature: null,
      baseline: null,
    };
  },
  watch: {
    form: {
      immediate: true,
      handler() {
        this.hydrateFromProp();
      },
    },
    local: {
      deep: true,
      handler() {
        if (!this.hydrating) {
          this.dirty = true;
        }
      },
    },
  },
  methods: {
    cloneForm(value) {
      if (!value || typeof value !== "object") {
        return {};
      }
      return JSON.parse(JSON.stringify(value));
    },
    formSignature(value) {
      return JSON.stringify(value || {});
    },
    normalizedSignature(value) {
      return JSON.stringify(value, (key, item) => (item === "" ? null : item));
    },
    hydrateFromProp() {
      const signature = this.formSignature(this.form);
      if (signature === this.lastSignature) {
        return;
      }
      if (this.dirty && !this.saving) {
        return;
      }
      this.hydrate();
    },
    hydrate() {
      this.hydrating = true;
      const source = this.cloneForm(this.form);
      const defaults = emptyForm();
      this.local = {
        ...defaults,
        ...source,
        origin: { ...defaults.origin, ...(source.origin || {}) },
        goods_lines: (source.goods_lines || []).length
          ? source.goods_lines.map((line, index) => ({
              ...emptyGoodsLine(),
              ...line,
              _key: line._key || `trade-goods-${index}`,
            }))
          : [emptyGoodsLine()],
      };
      this.lastSignature = this.formSignature(this.form);
      this.$nextTick(() => {
        this.hydrating = false;
        this.dirty = false;
        this.baseline = this.normalizedSignature(this.payload());
      });
    },
    addGoodsLine() {
      this.local.goods_lines.push(emptyGoodsLine());
    },
    cloneGoodsLine(index) {
      const source = this.local.goods_lines[index];
      const clone = {
        ...emptyGoodsLine(),
        stock_product_id: source.stock_product_id,
        name: source.name,
        hs_code: source.hs_code,
        origin: source.origin,
      };
      this.local.goods_lines.splice(index + 1, 0, clone);
    },
    removeGoodsLine(index) {
      this.local.goods_lines.splice(index, 1);
      if (!this.local.goods_lines.length) {
        this.local.goods_lines.push(emptyGoodsLine());
      }
    },
    async onProductPicked(line, productId) {
      line.stock_product_id = productId || null;
      if (!productId) {
        return;
      }
      try {
        const product = await this.$store.dispatch(GET_STOCK_PRODUCT, productId);
        if (!line.name) {
          line.name = product.display_name || product.label || "";
        }
        if (!line.hs_code && product.code) {
          line.hs_code = product.code;
        }
        if (!line.origin) {
          const country = product.origin_country && product.origin_country.name;
          const veterinary = product.veterinary_number;
          line.origin = [country, veterinary].filter(Boolean).join(" / ");
        }
      } catch (errors) {
        // Name stays editable even if the product lookup fails.
      }
    },
    payload() {
      const payload = JSON.parse(JSON.stringify(this.local));
      payload.goods_lines = (payload.goods_lines || []).map(({ _key, ...line }) => ({
        ...line,
        expiry_date: line.expiry_date || null,
        quantity_kg:
          line.quantity_kg === "" || line.quantity_kg === null || line.quantity_kg === undefined
            ? null
            : line.quantity_kg,
      }));
      return payload;
    },
    isDirty() {
      if (!this.baseline) {
        return this.dirty;
      }
      return this.normalizedSignature(this.payload()) !== this.baseline;
    },
  },
};
</script>

<style scoped>
.trade-goods-line {
  padding: 12px 4px 16px;
  margin-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.trade-goods-line:last-child {
  border-bottom: 0;
  margin-bottom: 0;
  padding-bottom: 4px;
}

.trade-goods-details {
  margin-top: 12px;
}
</style>
